import { lazy, Suspense, ReactNode } from 'react';
import { RouteObject } from 'react-router-dom';
import ProtectedLayout from '../components/layout/ProtectedLayout';
import PermissionRoute from '../components/common/PermissionRoute';
import { LoadingSpinner } from '../components/common/Loading';
import { Paths } from './paths';

const HomePage = lazy(() => import('../pages/home/page'));
const DashboardPage = lazy(() => import('../pages/dashboard/page'));
const ContactsPage = lazy(() => import('../pages/contacts/page'));
const CompaniesPage = lazy(() => import('../pages/companies/page'));
const OpportunitiesPage = lazy(() => import('../pages/opportunities/page'));
const ProductsPage = lazy(() => import('../pages/products/page'));
const ContractsPage = lazy(() => import('../pages/contracts/page'));
const ProjectsPage = lazy(() => import('../pages/projects/page'));
const ItemsPage = lazy(() => import('../pages/items/page'));
const SettingsPage = lazy(() => import('../pages/settings/page'));
const UsersPage = lazy(() => import('../pages/users/page'));
const PermissionsPage = lazy(() => import('../pages/permissions/page'));
const ApprovalsPage = lazy(() => import('../pages/approvals/page'));
const ApprovalsSubmitPage = lazy(() => import('../pages/approvals/submit/page'));
const FinanceOverviewPage = lazy(() => import('../pages/finance/overview/page'));
const PurchasePaymentPage = lazy(() => import('../pages/finance/purchase-payment/page'));
const ReimbursementPaymentPage = lazy(() => import('../pages/finance/reimbursement/page'));
const PayrollPage = lazy(() => import('../pages/finance/payroll/page'));
const ProjectOverviewPage = lazy(() => import('../pages/projects/overview/page'));
const ReportsPage = lazy(() => import('../pages/reports/page'));
const LoginPage = lazy(() => import('../pages/login/page'));
const ProfilePage = lazy(() => import('../pages/profile/page'));
const SuppliersPage = lazy(() => import('../pages/suppliers/page'));
const NotFoundPage = lazy(() => import('../pages/NotFound'));
const ForbiddenPage = lazy(() => import('../pages/Forbidden'));

// 内联 lazy imports（不再用 withSuspense 包裹，由 ProtectedLayout 的 Suspense 统一处理）
const LazyCatalogPage = lazy(() => import('../pages/items/catalog/page'));
const LazyMyRequisitionsPage = lazy(() => import('../pages/items/my-requisitions/page'));
const LazyApprovalsConfigPage = lazy(() => import('../pages/approvals/config/page'));
const LazyApprovalsDetailPage = lazy(() => import('../pages/approvals/detail/page'));
const LazyBillPoolPage = lazy(() => import('../pages/finance/bill-pool/page'));
const LazyInvoicePoolPage = lazy(() => import('../pages/finance/invoice-pool/page'));
const LazyCostTransferPage = lazy(() => import('../pages/finance/cost-transfer/page'));
const LazyAiPolicyPage = lazy(() => import('../pages/settings/ai-policy/page'));
const LazyAiTodosPage = lazy(() => import('../pages/settings/ai-todos/page'));
const LazyDirectEntriesPage = lazy(() => import('../pages/finance/direct-entries/page'));
const LazyLoansPage = lazy(() => import('../pages/finance/loans/page'));
const LazyLoanDetailPage = lazy(() => import('../pages/finance/loans/detail/page'));
const LazyLoanPlanPage = lazy(() => import('../pages/finance/loan-plan/page'));
const LazyCurrentAccountsPage = lazy(() => import('../pages/finance/current-accounts/page'));
const LazyBudgetAdminPage = lazy(() => import('../pages/finance/budget/page'));
const LazyLlmModelsPage = lazy(() => import('../pages/llm/models/page'));
const LazyLlmSkillsPage = lazy(() => import('../pages/llm/skills/page'));
const LazyLlmToolsPage = lazy(() => import('../pages/llm/tools/page'));
const LazyAgentPage = lazy(() => import('../pages/agent/page'));

const OpsPage = lazy(() => import('../pages/ops/page'));
const CronJobsPage = lazy(() => import('../pages/cron-jobs/page'));
const MyReimbursementsPage = lazy(() => import('../pages/my-reimbursements/page'));
const MyBudgetPage = lazy(() => import('../pages/my-budget/page'));

// 路由 → 所需权限码映射
// 多个权限码表示「满足任一即可」（hasAnyPermission）
// HOME / PROFILE / LOGIN / 403 / 404 等基础页面不挂权限
type PermGuarded = { path: string; element: ReactNode; permissions?: string[] };

const guardedRoutes: PermGuarded[] = [
  // CRM 模块
  { path: Paths.CRM_OVERVIEW, element: <DashboardPage />, permissions: ['oa:crm:overview'] },
  { path: Paths.CONTACTS, element: <ContactsPage />, permissions: ['oa:contacts:view'] },
  { path: Paths.COMPANIES, element: <CompaniesPage />, permissions: ['oa:companies:view'] },
  { path: Paths.OPPORTUNITIES, element: <OpportunitiesPage />, permissions: ['oa:opportunities:view'] },
  { path: Paths.PRODUCTS, element: <ProductsPage />, permissions: ['oa:products:view'] },
  { path: Paths.SUPPLIERS, element: <SuppliersPage />, permissions: ['oa:suppliers:view'] },

  // 项目与合同
  { path: Paths.PROJECTS, element: <ProjectsPage />, permissions: ['oa:projects:view'] },
  { path: Paths.PROJECTS_OVERVIEW, element: <ProjectOverviewPage />, permissions: ['oa:projects:overview:view'] },
  { path: Paths.CONTRACTS, element: <ContractsPage />, permissions: ['oa:contracts:view'] },

  // 物品
  { path: Paths.ITEMS, element: <ItemsPage />, permissions: ['oa:items:view'] },
  { path: Paths.ITEMS_CATALOG, element: <LazyCatalogPage />, permissions: ['oa:items:catalog'] },
  { path: Paths.ITEMS_MY_REQUISITIONS, element: <LazyMyRequisitionsPage />, permissions: ['oa:items:my-requisitions'] },

  // 审批
  { path: Paths.APPROVALS, element: <ApprovalsPage />, permissions: ['oa:approvals:view'] },
  { path: Paths.APPROVALS_SUBMIT, element: <ApprovalsSubmitPage />, permissions: ['oa:approvals:submit'] },
  { path: Paths.APPROVALS_CONFIG, element: <LazyApprovalsConfigPage />, permissions: ['oa:approvals:config'] },
  { path: '/approvals/detail/:id', element: <LazyApprovalsDetailPage />, permissions: ['oa:approvals:view'] },

  // 财务
  { path: Paths.FINANCE_OVERVIEW, element: <FinanceOverviewPage />, permissions: ['oa:finance:overview'] },
  { path: Paths.FINANCE_PURCHASE_PAYMENT, element: <PurchasePaymentPage />, permissions: ['oa:finance:purchase-payment'] },
  { path: Paths.FINANCE_REIMBURSEMENT, element: <ReimbursementPaymentPage />, permissions: ['oa:finance:reimbursement'] },
  { path: Paths.FINANCE_PAYROLL, element: <PayrollPage />, permissions: ['oa:finance:payroll'] },
  { path: Paths.FINANCE_BILL_POOL, element: <LazyBillPoolPage />, permissions: ['oa:finance:bill-pool:view'] },
  { path: Paths.FINANCE_INVOICE_POOL, element: <LazyInvoicePoolPage />, permissions: ['oa:finance:invoice-pool:view'] },
  { path: Paths.FINANCE_COST_TRANSFER, element: <LazyCostTransferPage />, permissions: ['oa:finance:cost-transfer'] },
  { path: Paths.FINANCE_DIRECT_ENTRIES, element: <LazyDirectEntriesPage />, permissions: ['oa:finance:direct-entries'] },
  { path: Paths.FINANCE_LOANS, element: <LazyLoansPage />, permissions: ['oa:finance:loans'] },
  { path: '/finance/loans/detail/:id', element: <LazyLoanDetailPage />, permissions: ['oa:finance:loans'] },
  { path: Paths.FINANCE_LOAN_PLAN, element: <LazyLoanPlanPage />, permissions: ['oa:finance:loan-plan'] },
  { path: Paths.FINANCE_CURRENT_ACCOUNTS, element: <LazyCurrentAccountsPage />, permissions: ['oa:finance:current-accounts'] },
  { path: Paths.FINANCE_BUDGET, element: <LazyBudgetAdminPage />, permissions: ['oa:budget:manage'] },

  // LLM / Agent
  { path: Paths.LLM_MODELS, element: <LazyLlmModelsPage />, permissions: ['oa:llm:models:view'] },
  { path: Paths.LLM_SKILLS, element: <LazyLlmSkillsPage />, permissions: ['oa:llm:skills:view'] },
  { path: Paths.LLM_TOOLS, element: <LazyLlmToolsPage />, permissions: ['oa:llm:tools:view'] },
  { path: Paths.AGENT_MANAGE, element: <LazyAgentPage />, permissions: ['oa:agent:view'] },

  // 系统
  { path: Paths.REPORTS, element: <ReportsPage />, permissions: ['oa:reports:view'] },
  { path: Paths.SETTINGS, element: <SettingsPage />, permissions: ['oa:home:view'] },
  { path: Paths.SETTINGS_AI_POLICY, element: <LazyAiPolicyPage />, permissions: ['oa:home:view'] },
  { path: Paths.SETTINGS_AI_TODOS, element: <LazyAiTodosPage />, permissions: ['oa:home:view'] },
  { path: Paths.USERS, element: <UsersPage />, permissions: ['oa:system:users'] },
  { path: Paths.PERMISSIONS, element: <PermissionsPage />, permissions: ['oa:system:permissions'] },
  { path: Paths.OPS, element: <OpsPage />, permissions: ['oa:system:ops'] },
  { path: Paths.CRON_JOBS, element: <CronJobsPage />, permissions: ['cron_jobs'] },
];

const routes: RouteObject[] = [
  // 登录页不需要 MainLayout
  { path: Paths.LOGIN, element: <Suspense fallback={<LoadingSpinner size="lg" color="cyan" text="加载中..." />}><LoginPage /></Suspense> },
  // 403 无权限页（独立路由，不走 ProtectedLayout 避免死循环）
  { path: '/403', element: <Suspense fallback={<LoadingSpinner />}><ForbiddenPage /></Suspense> },
  // 所有需要 MainLayout 的页面作为 ProtectedLayout 的子路由
  {
    element: <ProtectedLayout />,
    children: [
      { path: Paths.HOME, element: <HomePage /> },
      { path: Paths.MY_REIMBURSEMENTS, element: <MyReimbursementsPage /> },
      { path: Paths.MY_BUDGET, element: <MyBudgetPage /> },
      ...guardedRoutes.map(({ path, element, permissions }) => ({
        path,
        element: permissions && permissions.length > 0
          ? <PermissionRoute permissions={permissions}>{element}</PermissionRoute>
          : element,
      })),
      { path: Paths.PROFILE, element: <ProfilePage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];

export default routes;
